import { Box, Chip, Paper, SxProps, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Theme, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ReserveFeeRow } from '../types/charts'
import { lang } from '../utils/lang'

export interface ReserveFeeTableProps {
  rows: ReserveFeeRow[]
  /** Amounts exclude VAT (VAT-registered customer); otherwise the fee includes it. */
  vatRegistered: boolean
  /** The sum over all the customer's batteries: set apart from the per-battery tables. */
  total?: boolean
  /** One of several batteries: the fee is shared, the rows are this battery's share. */
  shared?: boolean
  sx?: SxProps<Theme>
}

/**
 * Monthly reserve compensation after the service fee: billed power, compensation,
 * fee (tagged when discounted) and what is paid out. Paid months are final, later
 * ones preliminary (marked with an asterisk), the current month in progress.
 */
export function ReserveFeeTable(props: ReserveFeeTableProps): React.ReactElement {
  const { rows, vatRegistered, total, shared, sx } = props
  const { t } = useTranslation()
  const preliminary = rows.some(r => r.status === 'preliminary')
  // Five columns fit a phone only with tight cells.
  const cell: SxProps<Theme> = { px: { xs: 0.5, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.875rem' }, whiteSpace: 'nowrap' }
  const head: SxProps<Theme> = {
    ...cell,
    whiteSpace: { xs: 'normal', sm: 'nowrap' },
    color: total ? 'primary.contrastText' : '#fff',
    fontWeight: total ? 'bold' : undefined
  }

  const payout = (row: ReserveFeeRow): string => {
    switch (row.status) {
      case 'in_progress':
        return t('Fee-InProgress')
      case 'preliminary':
        return `${lang.money(row.payout, 'EUR')}*`
      default:
        return lang.money(row.payout, 'EUR')
    }
  }

  return (
    <Box sx={sx} data-cy={total ? 'reserve-fee-total' : 'reserve-fee-table'}>
      {total && (
        <Typography variant="h6" sx={{ mb: 1 }}>{t('Fee-Total')}</Typography>
      )}
      <TableContainer
        component={Paper}
        sx={total ? { border: 2, borderColor: 'primary.main' } : undefined}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={total ? { backgroundColor: 'primary.main' } : undefined}>
              <TableCell sx={head}>{t('Fee-Month')}</TableCell>
              <TableCell sx={head} align="right">{t('Fee-Power')}</TableCell>
              <TableCell sx={head} align="right">{t('Fee-Revenue')}</TableCell>
              <TableCell sx={head} align="right">{t('Fee-Fee')}</TableCell>
              <TableCell sx={head} align="right">{t('Fee-Payout')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>{t('Fee-NoData')}</TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow key={row.month} data-cy={`reserve-fee-${row.month}`}>
                <TableCell sx={cell}>{row.month}</TableCell>
                <TableCell sx={cell} align="right">{lang.num(row.power_kw, 2, 'kW')}</TableCell>
                <TableCell sx={cell} align="right">{lang.money(row.revenue, 'EUR')}</TableCell>
                <TableCell sx={cell} align="right">
                  {lang.money(row.fee, 'EUR')}
                  {row.discount && (
                    <Chip
                      label={t('Fee-Discount')}
                      size="small"
                      color="error"
                      data-cy="fee-discount"
                      sx={{ ml: { xs: 0.5, sm: 1 }, height: 18, fontWeight: 'bold', fontSize: '0.65rem', transform: 'rotate(-6deg)', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ ...cell, fontWeight: total ? 'bold' : undefined }}>{payout(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
        {vatRegistered ? t('Fee-VatExcludedNote') : t('Fee-VatIncludedNote')}
      </Typography>
      {shared && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Fee-SharedNote')}
        </Typography>
      )}
      {preliminary && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Fee-PreliminaryNote')}
        </Typography>
      )}
    </Box>
  )
}
